import { useMemo, useRef } from "react";
import { FetchQueryOptions, QueryKey, useQuery, UseQueryResult } from "react-query";

import { getConfig } from "@app/api/config";

import { ApiOptions } from "@app/api/models/api";

export interface CustomizableQueryOptions {
  enabled?: boolean;
}

export type ReactQueryOptions<
  TQueryFnData = unknown,
  TError = any,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
  CustomizableQueryOptions;

export type QueryOptions = Omit<ReactQueryOptions, "queryFn">;

export type QueryFnContext = {
  apiOptions: ApiOptions;
};

export function useApiQuery<D, E = any>(
  queryFunc: (context: QueryFnContext) => Promise<unknown>,
  queryOptions: QueryOptions,
): UseQueryResult<D, E> {
  // Config
  const configRef = useRef(getConfig());
  const config = configRef.current;

  const reactQueryOptions: ReactQueryOptions = useMemo(() => {
    const defaultEnabled = queryOptions.enabled ?? true;
    const options: ReactQueryOptions = Object.assign({}, queryOptions, {
      enabled: config != null && defaultEnabled,
    });

    if (config == null) {
      options.queryFn = () => {
        return null;
      };
    } else {
      const apiOptions: ApiOptions = {
        config: { baseUrl: config.api.baseUrl },
      };

      options.queryFn = () =>
        queryFunc({
          apiOptions,
        });
    }

    return options;
  }, [queryOptions, config, queryFunc]);

  return useQuery(reactQueryOptions) as UseQueryResult<D, E>;
}